import React, { useState, useMemo, useEffect } from 'react'
import AdminLayout from '@/components/AdminLayout'
import { InterviewResult, useInterview } from '@/contexts/InterviewContext'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts'
import {
  Search,
  FileDown,
  ArrowDownAZ,
  ArrowUpZA,
  TrendingDown,
  TrendingUp,
  Filter,
  ArrowLeft,
  Calendar,
  ListIcon,
  LayoutGrid,
  ChevronDown,
  ChevronRight,
} from 'lucide-react'
import QuestionAnalytics from '@/components/QuestionAnalytics'
import QuestionResponseList from '@/components/QuestionResponseList'
import { Question } from '@/contexts/InterviewContext'
import { supabase } from '@/integrations/supabase/client'
import { getPaginationPages, getTotalPages, paginate } from '@/utils/pagination'
import { fetchAllRows, fetchAllRowsByIds } from '@/utils/supabasePagination'
import * as XLSX from 'xlsx'

type SortOption = 'idAsc' | 'idDesc' | 'correctAsc' | 'correctDesc'

interface TestInfo {
  id: string
  name: string
  created_at: string
}

const AdminAnalytics = () => {
  const [tests, setTests] = useState<TestInfo[]>([])
  const [selectedTestId, setSelectedTestId] = useState<string | null>(null)
  const [questions, setQuestions] = useState<Question[]>([])
  const [results, setResults] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [analyticsLoading, setAnalyticsLoading] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [sortBy, setSortBy] = useState<SortOption>('idAsc')
  const [selectedQuestion, setSelectedQuestion] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<string>('overview')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [viewMode, setViewMode] = useState<'card' | 'list'>('card')
  const testPerPageOptions = viewMode === 'card' ? [6, 9, 15] : [10, 20, 50, 100]
  const [testsPerPage, setTestsPerPage] = useState(testPerPageOptions[0])
  const [testPage, setTestPage] = useState(1)
  const [totalResponses, setTotalResponses] = useState<number>(0)
  const [answeredCandidates, setAnsweredCandidates] = useState<number>(0)
  const [questionPage, setQuestionPage] = useState(1)
  const questionPerPageOptions = [5, 10, 20, 50]
  const [questionsPerPage, setQuestionsPerPage] = useState(questionPerPageOptions[0])
  const [exportLoading, setExportLoading] = useState(false)
  type TestType = 'manual' | 'random' | 'hybrid' | null
  const [testType, setTestType] = useState<TestType>(null)

  // Fetch available tests
  useEffect(() => {
    const fetchTests = async () => {
      setLoading(true)
      try {
        const data = await fetchAllRows<TestInfo>(
          'tests',
          'id, name, created_at',
          (q) => q.order('created_at', { ascending: false })
        )
        setTests(data)
      } catch (error) {
        console.error('Error fetching tests:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchTests()
  }, [])

  async function fetchAllAnswers(testId: string) {
    const result = await fetchAllRows<any>(
      'employee_answers',
      `
        employee_test_id,
        question_id,
        is_correct,
        choice_id,
        employee_tests!inner(test_id, status)
      `,
      (query) =>
        query
          .eq('employee_tests.test_id', testId)
          .not('employee_tests.status', 'in', `(renewed,expired,pending)`)
    )
    return result
  }

  /**
   * Fetches all required analytics data for a test, based on its type:
   *  - manual: Only questions linked in 'test_questions'
   *  - random: All questions in categories defined in 'tests.categories'
   *  - hybrid: Union of both manual and random questions (duplicates removed)
   *
   * Populates: questions, results, and analytics states for the analytics UI.
   */
  async function fetchAnalyticsData(testId: string) {
    setAnalyticsLoading(true)

    try {
      // 1. Fetch test metadata to determine type and categories
      const { data: testMeta, error: testMetaErr } = await supabase
        .from('tests')
        .select('type, categories')
        .eq('id', testId)
        .single()
      if (testMetaErr) throw testMetaErr
      const { type, categories: categoriesObj } = testMeta || {}
      setTestType(type as TestType)

      // 2. Prepare questionRows according to test type
      // Structure: [{ question_id, questions: { id, text, category } }]
      let questionRows: {
        question_id: string
        questions: {
          id: string
          text: string
          category: string
        }
      }[] = []

      if (type === 'manual') {
        // MANUAL: Fetch only questions explicitly linked in 'test_questions'
        const { data: tqRows, error: tqErr } = await supabase
          .from('test_questions')
          .select(
            `
          question_id,
          questions (
            id,
            text,
            category
          )
        `
          )
          .eq('test_id', testId)
        if (tqErr) throw tqErr
        questionRows = (tqRows || []).map((row: any) => ({
          question_id: row.question_id,
          questions: Array.isArray(row.questions) ? row.questions[0] : row.questions,
        }))
      } else if (type === 'random') {
        // RANDOM: Fetch all questions whose category is in tests.categories
        const categories = categoriesObj ? Object.keys(categoriesObj) : []
        if (categories.length > 0) {
          const qRows = await fetchAllRows<any>(
            'questions',
            'id, text, category',
            (q) => q.in('category', categories)
          )
          questionRows = qRows.map((q) => ({
            question_id: q.id,
            questions: q,
          }))
        }
      } else if (type === 'hybrid') {
        // HYBRID: Combine both approaches, then deduplicate by question_id
        const categories = categoriesObj ? Object.keys(categoriesObj) : []

        const [manualRes, catRows] = await Promise.all([
          supabase
            .from('test_questions')
            .select(
              `
            question_id,
            questions (
              id,
              text,
              category
            )
          `
            )
            .eq('test_id', testId),
          categories.length > 0
            ? fetchAllRows<any>(
                'questions',
                'id, text, category',
                (q) => q.in('category', categories)
              )
            : Promise.resolve([] as any[]),
        ])
        const manualQs =
          (manualRes.data || []).map((row: any) => ({
            question_id: row.question_id,
            questions: Array.isArray(row.questions) ? row.questions[0] : row.questions,
          })) || []
        const catQs =
          catRows.map((q: any) => ({
            question_id: q.id,
            questions: q,
          })) || []
        // Deduplicate: Only keep unique questions by question_id
        questionRows = [...manualQs, ...catQs].filter(
          (q, idx, arr) => arr.findIndex((qq) => qq.question_id === q.question_id) === idx
        )
      } else {
        // Unknown type: No questions fetched
        questionRows = []
      }

      // 3. Extract unique question IDs for fetching choices and answers
      const questionIds = Array.from(new Set(questionRows.map((r) => r.question_id)))

      // 4. Fetch all choices for these questions
      const choicesData = await fetchAllRowsByIds<any>(
        'choices',
        'id, question_id, text, label',
        'question_id',
        questionIds
      )

      // 5. Fetch all answers for this test (employee_answers + all variants)
      const allAnswers = await fetchAllAnswers(testId)

      // Count unique answered candidates from the same fetch (avoids a second fetchAllAnswers call)
      const uniqueEts = new Set(allAnswers.map((row: any) => row.employee_test_id))
      setAnsweredCandidates(uniqueEts.size)

      // 6. Transform all answers into a per-candidate structure
      // Structure: { [employee_test_id]: { candidateId, responses: [...] } }
      const resultsByEmployeeTest = allAnswers.reduce(
        (acc: Record<string, InterviewResult>, ans: any) => {
          const eid = ans.employee_test_id as string
          if (!acc[eid]) {
            acc[eid] = { candidateId: eid, responses: [] }
          }
          acc[eid].responses.push({
            questionId: ans.question_id.toString(),
            isCorrect: ans.is_correct,
            choiceId: ans.choice_id.toString(),
            questionText: '',
          })
          return acc
        },
        {}
      )
      const transformedResults = Object.values(resultsByEmployeeTest)
      setResults(transformedResults)
      setTotalResponses(allAnswers.length)

      // 7. Map question_id to its list of choices
      const choicesByQuestion = (choicesData || []).reduce(
        (acc, c) => {
          ;(acc[c.question_id] ||= []).push({
            id: c.id.toString(),
            text: c.text,
            label: c.label,
          })
          return acc
        },
        {} as Record<string, { id: string; text: string; label: string }[]>
      )

      // 8. Transform questionRows into a unique array of Question objects with their choices
      const transformedQuestions = questionRows
        .map((r) => r.questions)
        .reduce((uniq: Question[], q) => {
          if (!uniq.find((x) => x.id === q.id)) {
            uniq.push({
              id: q.id,
              text: q.text,
              category: q.category,
              options: choicesByQuestion[q.id] || [],
              choices: choicesByQuestion[q.id] || [],
            })
          }
          return uniq
        }, [])

      // 9. Update all necessary state
      setQuestions(transformedQuestions)
      setResults(transformedResults)
    } catch (err) {
      console.error('Error fetching analytics data:', err)
    } finally {
      setAnalyticsLoading(false)
    }
  }

  const handleTestSelect = (testId: string) => {
    setSelectedTestId(testId)
    setSelectedQuestion(null)
    setActiveTab('overview')
    fetchAnalyticsData(testId)
  }

  const handleBackToTests = () => {
    setSelectedTestId(null)
    setQuestions([])
    setResults([])
    setSearchQuery('')
    setSelectedQuestion(null)
    setActiveTab('overview')
    setSelectedCategory('all')
  }

  const formatDate = (isoDate: string) => {
    const date = new Date(isoDate)
    return date.toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  // Extract unique categories
  const categories = useMemo(() => {
    const uniqueCategories = [...new Set(questions.map((q) => q.category || 'Uncategorized'))]
    return ['all', ...uniqueCategories]
  }, [questions])

  // Calculate overall statistics

  const correctResponses = results.reduce(
    (sum, result) => sum + result.responses.filter((r) => r.isCorrect).length,
    0
  )
  const incorrectResponses = totalResponses - correctResponses

  // Calculate per-question statistics
  const questionStats = questions.map((question, index) => {
    const responses = results.flatMap((result) =>
      result.responses.filter((response) => response.questionId === question.id)
    )

    const totalResponses = responses.length
    const correctCount = responses.filter((r) => r.isCorrect).length
    const correctRate = totalResponses > 0 ? (correctCount / totalResponses) * 100 : 0

    return {
      id: question.id,
      sequence: index + 1, // Use index + 1 as sequence for display purposes
      text: question.text,
      category: question.category || 'Uncategorized',
      totalResponses,
      correctCount,
      incorrectCount: totalResponses - correctCount,
      correctRate,
      incorrectRate: 100 - correctRate,
    }
  })

  // Calculate per-category statistics
  const categoryStats = useMemo(() => {
    const stats = categories
      .filter((cat) => cat !== 'all')
      .map((category) => {
        const categoryQuestions = questionStats.filter(
          (q) => q.category.toLocaleLowerCase('tr') === category.toLocaleLowerCase('tr')
        )

        const totalResponses = categoryQuestions.reduce((sum, q) => sum + q.totalResponses, 0)
        const correctCount = categoryQuestions.reduce((sum, q) => sum + q.correctCount, 0)
        const correctRate = totalResponses > 0 ? (correctCount / totalResponses) * 100 : 0

        return {
          category,
          questionCount: categoryQuestions.length,
          totalResponses,
          correctCount,
          incorrectCount: totalResponses - correctCount,
          correctRate,
          incorrectRate: 100 - correctRate,
        }
      })

    return stats
  }, [categories, questionStats])

  const handleQuestionSearch = () => {
    setSearchQuery(searchInput)
    setQuestionPage(1)
  }

  // Filter questions based on search query and category
  const filteredQuestions = questions.filter((question) => {
    const matchesSearch = question.text.toLocaleLowerCase('tr').includes(searchQuery.toLocaleLowerCase('tr'))
    const matchesCategory =
      selectedCategory === 'all' ||
      (question.category || 'Uncategorized').toLocaleLowerCase('tr') === selectedCategory.toLocaleLowerCase('tr')

    return matchesSearch && matchesCategory
  })

  // Sort questions based on selected option
  const sortedQuestions = [...filteredQuestions].sort((a, b) => {
    const statsA = questionStats.find((q) => q.id === a.id)
    const statsB = questionStats.find((q) => q.id === b.id)

    if (!statsA || !statsB) return 0

    switch (sortBy) {
      case 'idAsc':
        return statsA.sequence - statsB.sequence
      case 'idDesc':
        return statsB.sequence - statsA.sequence
      case 'correctAsc':
        return statsA.correctRate - statsB.correctRate
      case 'correctDesc':
        return statsB.correctRate - statsA.correctRate
      default:
        return 0
    }
  })

  //Pagination
  const testTotalPages = getTotalPages(tests.length, testsPerPage)
  const paginatedTests = paginate(tests, testPage, testsPerPage)

  useEffect(() => {
    setTestsPerPage(testPerPageOptions[0])
    setTestPage(1)
  }, [viewMode])

  // Group questions by category for display
  const groupedQuestions = useMemo(() => {
    const groups: Record<string, Question[]> = {}

    sortedQuestions.forEach((question) => {
      const category = question.category || 'Uncategorized'
      if (!groups[category]) {
        groups[category] = []
      }
      groups[category].push(question)
    })

    return groups
  }, [sortedQuestions])

  // Prepare data for overview charts
  const questionPerformanceData = questionStats
    .sort((a, b) => a.sequence - b.sequence)
    .map((stat) => ({
      name: `${stat.sequence}`,
      doğru: stat.correctRate.toFixed(0),
      yanlış: stat.incorrectRate.toFixed(0),
      totalResponses: stat.totalResponses,
      category: stat.category,
    }))

  const categoryPerformanceData = categoryStats
    .filter((stat) => stat.totalResponses > 0)
    .map((stat) => ({
      name: stat.category,
      doğru: stat.correctRate.toFixed(0),
      yanlış: stat.incorrectRate.toFixed(0),
      totalResponses: stat.totalResponses,
      questionCount: stat.questionCount,
    }))

  const overallDistributionData = [
    { name: 'Doğru', value: correctResponses, color: '#4ade80' },
    { name: 'Yanlış', value: incorrectResponses, color: '#f87171' },
  ]

  const categoryOptions = useMemo(() => {
    const cats = questionPerformanceData.map((q) => q.category).filter(Boolean)
    return ['Tüm Kategoriler', ...Array.from(new Set(cats))]
  }, [questionPerformanceData])

  const filteredData =
    selectedCategory === 'Tüm Kategoriler' || selectedCategory === 'all'
      ? questionPerformanceData
      : questionPerformanceData.filter(
          (item) =>
            (item.category || 'Uncategorized').toLocaleLowerCase('tr') === selectedCategory.toLocaleLowerCase('tr')
        )

  const COLORS = ['#4ade80', '#f87171']
  const CATEGORY_COLORS = [
    '#3b82f6',
    '#f97316',
    '#8b5cf6',
    '#ec4899',
    '#06b6d4',
    '#84cc16',
    '#f43f5e',
    '#14b8a6',
    '#f59e0b',
    '#6366f1',
  ]

  const htmlToPlainText = (html: string | null | undefined) => {
    if (!html) return ''
    return html
      .replace(/<[^>]+>/g, ' ') // remove tags
      .replace(/\s+/g, ' ') // collapse whitespace
      .trim()
  }

  // Export data as CSV
  const exportDataAsExcel = async () => {
    if (!selectedTestId || results.length === 0 || questions.length === 0) return

    setExportLoading(true)

    try {
      const selectedTest = tests.find((t) => t.id === selectedTestId)
      // Resolve candidate -> employee
      const allAnswersForExport = await fetchAllAnswers(selectedTestId)

      const candidateIds = Array.from(
        new Set(allAnswersForExport.map((r: any) => r.employee_test_id).filter(Boolean))
      ) as string[]

      if (candidateIds.length === 0) return

      const BATCH_SIZE = 100

      // Batch fetch employee_tests data
      let employeeTestMap: Record<string, { employee_id: string }> = {}
      for (let i = 0; i < candidateIds.length; i += BATCH_SIZE) {
        const batch = candidateIds.slice(i, i + BATCH_SIZE)
        const { data: etData, error: etError } = await supabase
          .from('employee_tests')
          .select('id, employee_id')
          .in('id', batch)

        if (etError) {
          console.error('Error fetching employee_tests batch:', etError)
        } else {
          ;(etData || []).forEach((row: any) => {
            employeeTestMap[row.id] = { employee_id: row.employee_id }
          })
        }
      }

      // Batch fetch employees data
      const employeeIds = Array.from(
        new Set(
          Object.values(employeeTestMap)
            .map((x) => x.employee_id)
            .filter(Boolean)
        )
      ) as string[]

      let employeeMap: Record<
        string,
        { id: string; sicil?: string | null; personel?: string | null }
      > = {}

      if (employeeIds.length > 0) {
        for (let i = 0; i < employeeIds.length; i += BATCH_SIZE) {
          const batch = employeeIds.slice(i, i + BATCH_SIZE)
          const { data: employeesData, error: employeesError } = await supabase
            .from('employee')
            .select('id, sicil, personel')
            .in('id', batch)

          if (employeesError) {
            console.error('Error fetching employees batch:', employeesError)
          } else {
            ;(employeesData || []).forEach((emp: any) => {
              employeeMap[emp.id] = emp
            })
          }
        }
      }

      let assignedQuestionsMap: Record<string, Set<string>> = {}

      for (let i = 0; i < candidateIds.length; i += BATCH_SIZE) {
        const batch = candidateIds.slice(i, i + BATCH_SIZE)
        const { data: etqData, error: etqError } = await supabase
          .from('employee_test_questions')
          .select('employee_test_id, question_id')
          .in('employee_test_id', batch)

        if (etqError) {
          console.error('Error fetching employee_test_questions batch:', etqError)
        } else {
          ;(etqData || []).forEach((row: any) => {
            if (!assignedQuestionsMap[row.employee_test_id]) {
              assignedQuestionsMap[row.employee_test_id] = new Set()
            }
            assignedQuestionsMap[row.employee_test_id].add(row.question_id)
          })
        }
      }

      // Build choice map
      const choiceIds = Array.from(
        new Set(allAnswersForExport.map((a: any) => a.choice_id).filter(Boolean))
      ) as string[]

      let choiceMap: Record<string, { id: string; text: string; label?: string }> = {}
      if (choiceIds.length > 0) {
        for (let i = 0; i < choiceIds.length; i += BATCH_SIZE) {
          const batch = choiceIds.slice(i, i + BATCH_SIZE)
          const { data: choicesData, error: choicesErr } = await supabase
            .from('choices')
            .select('id, text, label')
            .in('id', batch)

          if (!choicesErr && choicesData) {
            ;(choicesData || []).forEach((c: any) => {
              choiceMap[c.id] = { id: c.id, text: c.text, label: c.label }
            })
          }
        }
      }

      // Build question map from existing questions array
      const questionMap: Record<string, Question> = {}
      questions.forEach((q) => {
        questionMap[q.id] = q
      })

      // Build correct choice map
      const allQuestionIds = questions.map((q) => q.id)
      let correctChoiceMap: Record<string, { id: string; text: string }> = {}

      for (let i = 0; i < allQuestionIds.length; i += BATCH_SIZE) {
        const batch = allQuestionIds.slice(i, i + BATCH_SIZE)
        const { data: correctChoices, error: correctErr } = await supabase
          .from('choices')
          .select('id, question_id, text')
          .in('question_id', batch)
          .eq('is_correct', true)

        if (!correctErr && correctChoices) {
          correctChoices.forEach((c: any) => {
            correctChoiceMap[c.question_id] = { id: c.id, text: c.text }
          })
        }
      }

      // Group answers by candidate and question
      const answersByCandidate: Record<string, Record<string, any>> = {}
      allAnswersForExport.forEach((ans: any) => {
        const etId = ans.employee_test_id as string
        const qId = ans.question_id as string
        if (!answersByCandidate[etId]) answersByCandidate[etId] = {}
        answersByCandidate[etId][qId] = ans
      })

      // Build rows
      const headers = [
        'Sicil',
        'Personel',
        'Sınav',
        'Kategori',
        'Soru',
        'Cevap',
        'Doğru Cevap',
        'Doğru/Yanlış/Boş',
      ]
      const rows: (string | number)[][] = [headers]

      for (const candidateId of candidateIds) {
        const et = employeeTestMap[candidateId]
        const employee = et ? employeeMap[et.employee_id] : undefined

        const sicil = (employee?.sicil || candidateId || '') as string
        const personel = (employee?.personel || '') as string

        // Get assigned questions for this candidate
        const assignedQuestions = assignedQuestionsMap[candidateId]
        const candidateAnswers = answersByCandidate[candidateId] || {}

        // If we have assigned questions info, use it; otherwise fall back to answered questions only
        const questionsToCheck: string[] = assignedQuestions
          ? Array.from(assignedQuestions)
          : Object.keys(candidateAnswers)

        questionsToCheck.forEach((qId: string) => {
          const question = questionMap[qId]

          if (!question) {
            return
          }

          const ans = candidateAnswers[qId]
          const correctChoice = correctChoiceMap[qId]
          const correctChoiceText = correctChoice ? correctChoice.text : ''
          const questionPlain = htmlToPlainText(question.text as any)

          let selectedChoiceText = ''
          let status = 'Boş'

          if (ans) {
            // User answered this question
            selectedChoiceText =
              ans.choice_id && choiceMap[ans.choice_id] ? choiceMap[ans.choice_id].text : ''
            status = ans.is_correct ? 'Doğru' : 'Yanlış'
          }

          rows.push([
            sicil,
            personel,
            selectedTest?.name || '',
            question.category || 'Diğer',
            questionPlain,
            selectedChoiceText,
            correctChoiceText,
            status,
          ])
        })
      }

      const worksheet = XLSX.utils.aoa_to_sheet(rows)
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Cevaplar')

      const fileName = `soru-analizi-${selectedTest?.name || 'sinav'}-${
        new Date().toISOString().split('T')[0]
      }.xlsx`

      XLSX.writeFile(workbook, fileName)
    } catch (err) {
      console.error('Error exporting Excel:', err)
    } finally {
      setExportLoading(false)
    }
  }

  const handleQuestionSelect = (questionId: string) => {
    setSelectedQuestion(selectedQuestion === questionId ? null : questionId)
  }

  const grouped = sortedQuestions.reduce(
    (acc, question) => {
      const category = question.category || 'Uncategorized'
      if (!acc[category]) acc[category] = []
      acc[category].push(question)
      return acc
    },
    {} as Record<string, typeof sortedQuestions>
  )

  const flatWithHeaders: {
    type: 'header' | 'question'
    category?: string
    question?: any
    count?: number
  }[] = []
  Object.entries(grouped).forEach(([category, questions]) => {
    flatWithHeaders.push({ type: 'header', category, count: questions.length })
    questions.forEach((question) => {
      flatWithHeaders.push({ type: 'question', question })
    })
  })

  const paginatedFlat = flatWithHeaders.slice(
    (questionPage - 1) * questionsPerPage,
    questionPage * questionsPerPage
  )

  const selectedQuestionData = selectedQuestion
    ? questions.find((q) => q.id === selectedQuestion)
    : null

  // Show loading state while fetching tests
  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-96">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      </AdminLayout>
    )
  }

  // Show test selection if no test is selected
  if (!selectedTestId) {
    return (
      <AdminLayout>
        <div className="space-y-6">
          <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
            <h1 className="text-2xl font-bold">Sınav Analizi</h1>
            <div className="flex items-center gap-2">
              <Button
                variant={viewMode === 'card' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('card')}
              >
                <LayoutGrid className="h-4 w-4 mr-1" />
                Kart
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('list')}
              >
                <ListIcon className="h-4 w-4 mr-1" />
                Liste
              </Button>
              <div className="flex items-center gap-2 ml-4">
                <span className="text-sm text-gray-500">Sayfa başı:</span>
                <Select
                  value={testsPerPage.toString()}
                  onValueChange={(value) => {
                    setTestsPerPage(Number(value))
                    setTestPage(1)
                  }}
                >
                  <SelectTrigger className="w-24">
                    <SelectValue placeholder="Sayfa başı" />
                  </SelectTrigger>
                  <SelectContent>
                    {testPerPageOptions.map((opt) => (
                      <SelectItem key={opt} value={opt.toString()}>
                        {opt}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {tests.length > 0 ? (
            viewMode === 'card' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {paginatedTests.map((test) => (
                  <Card
                    key={test.id}
                    className="cursor-pointer hover:shadow-lg transition-shadow duration-200"
                    onClick={() => handleTestSelect(test.id)}
                  >
                    <CardHeader>
                      <CardTitle className="text-lg font-medium flex items-center gap-2">
                        <Calendar className="h-5 w-5 text-gray-500" />
                        {test.name}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-gray-500 mb-4">{formatDate(test.created_at)}</p>
                      <Button className="w-full" variant="outline">
                        Analizi Görüntüle
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <div className="rounded-2xl shadow-lg border border-gray-200 bg-white overflow-hidden">
                  <table className="min-w-full bg-white">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="p-4 text-left rounded-tl-2xl">Sınav Adı</th>
                        <th className="p-4 text-left">Oluşturulma</th>
                        <th className="p-4 rounded-tr-2xl"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedTests.map((test, idx) => (
                        <tr
                          key={test.id}
                          className={
                            'hover:bg-gray-50 cursor-pointer transition ' +
                            (idx === paginatedTests.length - 1 ? 'border-b-0' : '')
                          }
                          onClick={() => handleTestSelect(test.id)}
                        >
                          <td
                            className={
                              'p-4 border-b border-gray-100 ' +
                              (idx === paginatedTests.length - 1 ? 'rounded-bl-2xl' : '')
                            }
                          >
                            {test.name}
                          </td>
                          <td className="p-4 border-b border-gray-100">
                            {formatDate(test.created_at)}
                          </td>
                          <td
                            className={
                              'p-4 border-b border-gray-100 ' +
                              (idx === paginatedTests.length - 1 ? 'rounded-br-2xl' : '')
                            }
                          >
                            <Button size="sm" variant="outline">
                              Analizi Görüntüle
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500">Analiz edilecek sınav bulunamadı.</p>
            </div>
          )}

          {/* PAGINATION */}
          {testTotalPages > 1 && (
            <div className="flex justify-center items-center gap-2 mt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setTestPage((p) => Math.max(p - 1, 1))}
                disabled={testPage === 1}
              >
                Önceki
              </Button>
              {getPaginationPages(testPage, testTotalPages).map((page, idx) =>
                page === '...' ? (
                  <span key={'ellipsis-' + idx} className="px-2 text-gray-400">
                    ...
                  </span>
                ) : (
                  <Button
                    key={page + '-' + idx}
                    variant={testPage === page ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setTestPage(page)}
                  >
                    {page}
                  </Button>
                )
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setTestPage((p) => Math.min(p + 1, testTotalPages))}
                disabled={testPage === testTotalPages}
              >
                Sonraki
              </Button>
            </div>
          )}
        </div>
      </AdminLayout>
    )
  }

  const selectedTest = tests.find((t) => t.id === selectedTestId)

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={handleBackToTests}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Sınavlara Dön
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Soru Analizi</h1>
              {selectedTest && (
                <p className="text-sm text-gray-500 mt-1">
                  {selectedTest.name} - {formatDate(selectedTest.created_at)}
                </p>
              )}
            </div>
          </div>
          <Button
            onClick={exportDataAsExcel}
            disabled={results.length === 0 || analyticsLoading || exportLoading}
            variant="outline"
          >
            {exportLoading ? (
              <>
                <div className="mr-2 h-4 w-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                Hazırlanıyor...
              </>
            ) : (
              <>
                <FileDown className="mr-2 h-4 w-4" />
                Raporu Dışa Aktar
              </>
            )}
          </Button>
        </div>

        {analyticsLoading ? (
          <div className="flex items-center justify-center min-h-96">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
            <TabsList className="grid w-full md:w-auto md:inline-grid grid-cols-3 md:grid-cols-3">
              <TabsTrigger value="overview">Genel Bakış</TabsTrigger>
              <TabsTrigger value="categories">Kategori Analizi</TabsTrigger>
              <TabsTrigger value="questions">Soru Analizi</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Toplam Çalışan</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{answeredCandidates}</div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Doğru Yanıt Oranı</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-green-600">
                      {totalResponses > 0
                        ? Math.round((correctResponses / totalResponses) * 100)
                        : 0}
                      %
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Toplam Yanıt</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{totalResponses}</div>
                  </CardContent>
                </Card>
              </div>

              {totalResponses > 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Doğru/Yanlış Yanıt Oranı</CardTitle>
                    </CardHeader>
                    <CardContent className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={overallDistributionData}
                            cx="50%"
                            cy="50%"
                            labelLine={true}
                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            outerRadius={100}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {overallDistributionData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => [`${value} yanıt`, '']} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <div className="flex justify-between items-center">
                        <CardTitle className="text-lg">Soru Bazında Doğru Oranı (%)</CardTitle>
                        <div className="flex items-center gap-2">
                          <select
                            value={selectedCategory}
                            onChange={(e) => setSelectedCategory(e.target.value)}
                            className="border rounded p-1 text-sm"
                          >
                            {categoryOptions.map((cat) => (
                              <option key={cat} value={cat}>
                                {cat}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={filteredData}
                          margin={{ top: 10, right: 30, left: 0, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <Tooltip
                            formatter={(value, name, props) => {
                              if (name === 'doğru') return [`%${value}`, 'Doğru Oranı']
                              return [value, '']
                            }}
                            content={({ active, payload }) => {
                              if (active && payload && payload.length) {
                                return (
                                  <div className="bg-white p-2 border rounded shadow-sm">
                                    <p className="font-medium">{`Soru ${payload[0]?.payload.name}`}</p>
                                    <p className="text-sm text-gray-500">{`Kategori: ${payload[0]?.payload.category}`}</p>
                                    <p className="text-sm">{`Doğru Oranı: %${payload[0]?.value}`}</p>
                                    <p className="text-xs">{`(${payload[0]?.payload.totalResponses} yanıt)`}</p>
                                  </div>
                                )
                              }
                              return null
                            }}
                          />
                          <Legend />
                          <Bar dataKey="doğru" fill="#4ade80" name="Doğru %" />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-500">Henüz analiz edilecek veri bulunmamaktadır.</p>
                </div>
              )}
            </TabsContent>

            <TabsContent value="categories" className="space-y-6">
              {categoryPerformanceData.length > 0 ? (
                <div className="space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Kategori Bazında Doğru Oranı (%)</CardTitle>
                    </CardHeader>
                    <CardContent className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={categoryPerformanceData}
                          margin={{
                            top: 10,
                            right: 30,
                            left: 0,
                            bottom: 5,
                          }}
                          layout="vertical"
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" domain={[0, 100]} />
                          <YAxis type="category" dataKey="name" width={150} />
                          <Tooltip
                            formatter={(value) => [`%${value}`, 'Doğru Oranı']}
                            content={({ active, payload }) => {
                              if (active && payload && payload.length) {
                                return (
                                  <div className="bg-white p-2 border rounded shadow-sm">
                                    <p className="font-medium">{payload[0]?.payload.name}</p>
                                    <p className="text-sm">{`Doğru Oranı: %${payload[0]?.value}`}</p>
                                    <p className="text-xs">{`(${payload[0]?.payload.totalResponses} yanıt)`}</p>
                                    <p className="text-xs">{`${payload[0]?.payload.questionCount} soru`}</p>
                                  </div>
                                )
                              }
                              return null
                            }}
                          />
                          <Legend />
                          <Bar dataKey="doğru" fill="#4ade80" name="Doğru %" />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {categoryStats
                      .sort((a, b) => b.correctRate - a.correctRate)
                      .map((stat, index) => (
                        <Card key={stat.category} className="overflow-hidden">
                          <div
                            className="h-2"
                            style={{
                              backgroundColor: CATEGORY_COLORS[index % CATEGORY_COLORS.length],
                            }}
                          />
                          <CardHeader className="py-4">
                            <CardTitle className="text-base font-medium flex items-center justify-between">
                              {stat.category}
                              <Badge variant="outline">{stat.questionCount} soru</Badge>
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <p className="text-sm text-gray-500 mb-1">Doğru Oranı</p>
                                <p className="text-xl font-semibold text-green-600">
                                  %{stat.correctRate.toFixed(0)}
                                </p>
                              </div>
                              <div>
                                <p className="text-sm text-gray-500 mb-1">Yanıt Sayısı</p>
                                <p className="text-xl font-semibold">{stat.totalResponses}</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-500">
                    Henüz analiz edilecek kategori verisi bulunmamaktadır.
                  </p>
                </div>
              )}
            </TabsContent>

            <TabsContent value="questions" className="space-y-6">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                <div className="flex items-center gap-2 w-full md:w-auto">
                  <Input
                    placeholder="Soru ara..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleQuestionSearch()}
                    className="max-w-xs"
                  />
                  <Button onClick={handleQuestionSearch} variant="outline" className="shrink-0">
                    <Search className="h-4 w-4 mr-1" />
                    Ara
                  </Button>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">Sayfa başı:</span>
                    <Select
                      value={questionsPerPage.toString()}
                      onValueChange={(v) => {
                        setQuestionsPerPage(Number(v))
                        setQuestionPage(1)
                      }}
                    >
                      <SelectTrigger className="w-20">
                        <SelectValue placeholder="Sayfa başı" />
                      </SelectTrigger>
                      <SelectContent>
                        {questionPerPageOptions.map((opt) => (
                          <SelectItem key={opt} value={opt.toString()}>
                            {opt}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
                  <div className="flex items-center gap-2">
                    <Filter className="h-5 w-5 text-gray-400" />
                    <Select
                      value={selectedCategory}
                      onValueChange={(v) => {
                        setSelectedCategory(v)
                        setQuestionPage(1)
                      }}
                    >
                      <SelectTrigger className="w-40 md:w-52">
                        <SelectValue placeholder="Kategori seçin" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tüm Kategoriler</SelectItem>
                        {categories
                          .filter((cat) => cat !== 'all')
                          .map((category) => (
                            <SelectItem key={category} value={category}>
                              {category}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">Sırala:</span>
                    <Select value={sortBy} onValueChange={(value: SortOption) => setSortBy(value)}>
                      <SelectTrigger className="w-52">
                        <SelectValue placeholder="Sıralama seçin" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="idAsc">
                          <div className="flex items-center">
                            <ArrowDownAZ className="mr-2 h-4 w-4" />
                            <span>Soru Numarası (Artan)</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="idDesc">
                          <div className="flex items-center">
                            <ArrowUpZA className="mr-2 h-4 w-4" />
                            <span>Soru Numarası (Azalan)</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="correctAsc">
                          <div className="flex items-center">
                            <TrendingDown className="mr-2 h-4 w-4" />
                            <span>Doğru Oranı (Düşükten Yükseğe)</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="correctDesc">
                          <div className="flex items-center">
                            <TrendingUp className="mr-2 h-4 w-4" />
                            <span>Doğru Oranı (Yüksekten Düşüğe)</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                {paginatedFlat.length > 0 ? (
                  paginatedFlat.map((item, idx) =>
                    item.type === 'header' ? (
                      <div
                        key={'header-' + item.category + '-' + idx}
                        className="sticky top-0 z-10 bg-white py-2"
                      >
                        <h3 className="text-lg font-semibold flex items-center">
                          <Badge variant="outline" className="mr-2 text-base px-3 py-1">
                            {item.category}
                          </Badge>
                          <span className="text-gray-500 text-sm">{item.count} soru</span>
                        </h3>
                      </div>
                    ) : (
                      <Card
                        key={item.question.id}
                        className="mb-2 cursor-pointer transition-shadow"
                        onClick={() => handleQuestionSelect(item.question.id)}
                      >
                        <CardHeader className="bg-gray-50 py-3 px-4">
                          <div className="flex justify-between items-center">
                            <div className="space-y-1">
                              <CardTitle className="text-base font-medium flex items-center">
                                Soru{' '}
                                {questionStats.find((q) => q.id === item.question.id)?.sequence}:{' '}
                                <div
                                  dangerouslySetInnerHTML={{ __html: item.question.text }}
                                  className="prose prose-sm max-w-none prose-headings:my-2 prose-p:my-1 prose-ol:my-2 prose-ul:my-2 prose-li:my-0.5"
                                />
                              </CardTitle>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-sm flex flex-col items-end">
                                <div>
                                  {questionStats.find((q) => q.id === item.question.id)
                                    ?.totalResponses || 0}{' '}
                                  yanıt
                                </div>
                                {(() => {
                                  const stats = questionStats.find((q) => q.id === item.question.id)
                                  if (stats && stats.totalResponses > 0) {
                                    return (
                                      <div
                                        className={
                                          stats.correctRate > 50 ? 'text-green-600' : 'text-red-600'
                                        }
                                      >
                                        %{stats.correctRate.toFixed(0)} doğru
                                      </div>
                                    )
                                  }
                                  return null
                                })()}
                              </div>
                              <button
                                className="ml-3 transition-transform"
                                style={{
                                  outline: 'none',
                                  border: 'none',
                                  background: 'none',
                                  padding: 0,
                                  cursor: 'pointer',
                                  display: 'flex',
                                }}
                                tabIndex={-1}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleQuestionSelect(item.question.id)
                                }}
                                aria-label={
                                  selectedQuestion === item.question.id
                                    ? 'Detayı gizle'
                                    : 'Detayı göster'
                                }
                              >
                                {selectedQuestion === item.question.id ? (
                                  <ChevronDown className="w-5 h-5 text-gray-400" />
                                ) : (
                                  <ChevronRight className="w-5 h-5 text-gray-400" />
                                )}
                              </button>
                            </div>
                          </div>
                        </CardHeader>
                        {selectedQuestion === item.question.id && selectedQuestionData && (
                          <div className="mt-4 space-y-6">
                            <QuestionAnalytics question={selectedQuestionData} results={results} />
                          </div>
                        )}
                      </Card>
                    )
                  )
                ) : (
                  <div className="text-center py-12">
                    <p className="text-gray-500 mb-4">
                      {searchQuery || selectedCategory !== 'all'
                        ? 'Arama kriterine uygun soru bulunamadı.'
                        : 'Henüz soru bulunmamaktadır.'}
                    </p>
                    {(searchQuery || selectedCategory !== 'all') && (
                      <Button
                        variant="outline"
                        onClick={() => {
                          setSearchInput('')
                          setSearchQuery('')
                          setSelectedCategory('all')
                        }}
                      >
                        Filtreleri Temizle
                      </Button>
                    )}
                  </div>
                )}
              </div>
              {/* Pagination Controls */}
              {getTotalPages(flatWithHeaders.length, questionsPerPage) > 1 && (
                <div className="flex justify-center items-center gap-2 mt-6">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setQuestionPage((p) => Math.max(p - 1, 1))}
                    disabled={questionPage === 1}
                  >
                    Önceki
                  </Button>
                  {getPaginationPages(
                    questionPage,
                    getTotalPages(flatWithHeaders.length, questionsPerPage)
                  ).map((pg, idx) =>
                    pg === '...' ? (
                      <span key={`ellipsis-${idx}`} className="px-2 text-gray-400">
                        ...
                      </span>
                    ) : (
                      <Button
                        key={pg + '-' + idx}
                        variant={questionPage === pg ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setQuestionPage(Number(pg))}
                      >
                        {pg}
                      </Button>
                    )
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setQuestionPage((p) =>
                        Math.min(p + 1, getTotalPages(flatWithHeaders.length, questionsPerPage))
                      )
                    }
                    disabled={
                      questionPage === getTotalPages(flatWithHeaders.length, questionsPerPage)
                    }
                  >
                    Sonraki
                  </Button>
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}
      </div>
    </AdminLayout>
  )
}

export default AdminAnalytics
